import { useCallback, useEffect, useState } from "react";
import { Home, Zap, Phone, Wifi, ShieldCheck, Plus, CheckCircle2, Clock, AlertTriangle, Send, Trash2 } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { StatePill } from "@/components/membership/Entitlement";
import { UpgradeModal } from "@/components/membership/UpgradeModal";
import { useEntitlements } from "@/hooks/useEntitlements";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const ICONS = { rent: Home, utility: Zap, phone: Phone, internet: Wifi };
const STAGE_META = {
  enrolled: { label: "Enrolled", tone: "bg-slate-100 text-slate-600", step: 0 },
  authorized: { label: "Authorized", tone: "bg-blue-50 text-[#1a5fd0]", step: 1 },
  verification_pending: { label: "Verification pending", tone: "bg-amber-50 text-amber-700", step: 2 },
  verified: { label: "Verified", tone: "bg-emerald-50 text-emerald-700", step: 3 },
  awaiting_provider: { label: "Awaiting reporting provider", tone: "bg-amber-50 text-amber-700", step: 4 },
  submitted: { label: "Submitted", tone: "bg-blue-50 text-[#1a5fd0]", step: 4 },
  reported: { label: "Reported", tone: "bg-emerald-50 text-emerald-700", step: 5 },
  exception: { label: "Exception", tone: "bg-red-50 text-red-700", step: 2 },
};
const STEPS = ["Enroll", "Authorize", "Verify", "Verified", "Submit", "Reported"];
const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]";

function Progress({ stage }) {
  const step = STAGE_META[stage]?.step ?? 0;
  return (
    <div className="mt-3 flex items-center gap-1" data-testid="rbr-progress">
      {STEPS.map((s, i) => (
        <div key={s} className="flex flex-1 flex-col gap-1">
          <div className={`h-1.5 rounded-full ${i <= step ? (stage === "exception" && i === step ? "bg-red-500" : "bg-[#1a5fd0]") : "bg-slate-200"}`} />
          <span className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{s}</span>
        </div>
      ))}
    </div>
  );
}

function EnrollForm({ types, onDone }) {
  const [form, setForm] = useState({ type: "rent", payee: "", monthly_amount: "", due_day: 1 });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/rent-bill/enroll", { ...form, monthly_amount: Number(form.monthly_amount), due_day: Number(form.due_day) });
      toast.success("Payment enrolled — authorize reporting next."); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not enroll."); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-4" data-testid="rbr-enroll-form">
      <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="rbr-type" className={inputCls}>
        {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
      </select>
      <input required placeholder={types.find((t) => t.key === form.type)?.payee_label || "Payee"} value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })} data-testid="rbr-payee" className={inputCls} />
      <input required type="number" min="1" step="0.01" placeholder="Monthly amount ($)" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })} data-testid="rbr-amount" className={inputCls} />
      <div className="flex gap-2">
        <input required type="number" min="1" max="31" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} data-testid="rbr-due-day" className={inputCls} title="Due day" />
        <button type="submit" disabled={busy} data-testid="rbr-enroll-btn" className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#1a5fd0] px-4 py-2 font-head text-[13px] font-bold text-white hover:bg-[#1550b8] disabled:opacity-60"><Plus size={14} /> Enroll</button>
      </div>
    </form>
  );
}

function Enrollment({ e, furnisherLive, reload }) {
  const Icon = ICONS[e.type] || Home;
  const meta = STAGE_META[e.stage] || STAGE_META.enrolled;
  const [pay, setPay] = useState({ period: new Date().toISOString().slice(0, 7), amount: e.monthly_amount, paid_on: new Date().toISOString().slice(0, 10) });
  const [ver, setVer] = useState({ method: "bank_statement", evidence_note: "" });
  const act = async (fn, ok) => { try { await fn(); ok && toast.success(ok); reload(); } catch (err) { toast.error(err?.response?.data?.detail || "Action failed."); } };

  return (
    <Card className="card-spotlight p-5" data-testid={`rbr-enrollment-${e.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#080d1a] text-white"><Icon size={18} /></span>
          <div>
            <div className="font-head text-[15px] font-bold text-slate-900">{e.payee}</div>
            <div className="text-[12px] text-slate-500">{e.type_label} · ${e.monthly_amount.toLocaleString()}/mo · due day {e.due_day}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${meta.tone}`} data-testid="rbr-stage">{meta.label}</span>
          <button onClick={() => act(() => api.delete(`/rent-bill/${e.id}`), "Enrollment removed")} data-testid="rbr-delete-btn" className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
        </div>
      </div>
      <Progress stage={e.stage} />

      {e.stage === "exception" && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-700" data-testid="rbr-exception"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {e.exception?.reason} — fix the issue and request verification again.</div>
      )}

      {e.stage === "enrolled" && (
        <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/50 p-4">
          <div className="text-[13px] font-semibold text-slate-800">Step 2 — Authorize reporting</div>
          <p className="mt-1 text-[12px] text-slate-500">You authorize MMT to share <b>verified</b> payment history for this payee with a consumer-reporting furnisher. Nothing is shared until verified and a provider is live.</p>
          <button onClick={() => act(() => api.post(`/rent-bill/${e.id}/authorize`, { consent: true }), "Reporting authorized")} data-testid="rbr-authorize-btn" className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#1a5fd0] px-4 py-2 font-head text-[13px] font-bold text-white hover:bg-[#1550b8]"><ShieldCheck size={14} /> I authorize</button>
        </div>
      )}

      {e.stage !== "enrolled" && (
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[13px] font-semibold text-slate-800">Payments logged ({e.payments.length})</div>
            <div className="mt-2 max-h-28 space-y-1 overflow-y-auto">
              {e.payments.length === 0 && <div className="text-[12px] text-slate-400">No payments yet.</div>}
              {e.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-[12px]" data-testid="rbr-payment-row">
                  <span className="font-mono text-slate-600">{p.period}</span><span className="text-slate-700">${p.amount}</span>
                  <span className={`font-mono text-[10px] uppercase ${p.status === "verified" ? "text-emerald-600" : "text-slate-400"}`}>{p.status}</span>
                </div>
              ))}
            </div>
            {["authorized", "exception", "verified", "awaiting_provider"].includes(e.stage) && (
              <div className="mt-3 flex flex-wrap gap-2">
                <input type="month" value={pay.period} onChange={(ev) => setPay({ ...pay, period: ev.target.value })} data-testid="rbr-pay-period" className={inputCls + " !w-36"} />
                <input type="number" step="0.01" value={pay.amount} onChange={(ev) => setPay({ ...pay, amount: ev.target.value })} data-testid="rbr-pay-amount" className={inputCls + " !w-28"} />
                <input type="date" value={pay.paid_on} onChange={(ev) => setPay({ ...pay, paid_on: ev.target.value })} data-testid="rbr-pay-date" className={inputCls + " !w-40"} />
                <button onClick={() => act(() => api.post(`/rent-bill/${e.id}/payments`, { ...pay, amount: Number(pay.amount) }), "Payment logged")} data-testid="rbr-log-payment-btn" className="rounded-lg border border-slate-300 px-3 py-2 text-[12px] font-semibold text-slate-700 hover:bg-slate-50">Log payment</button>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 p-4">
            {["authorized", "exception"].includes(e.stage) && (
              <>
                <div className="text-[13px] font-semibold text-slate-800">Step 3 — Request verification</div>
                <p className="mt-1 text-[12px] text-slate-500">MMT reviews your evidence before anything is reported. A logged payment is not a verified one.</p>
                <select value={ver.method} onChange={(ev) => setVer({ ...ver, method: ev.target.value })} data-testid="rbr-verify-method" className={inputCls + " mt-2"}>
                  <option value="bank_statement">Bank statement</option><option value="lease">Lease / contract</option><option value="receipt">Receipts</option><option value="landlord_confirmation">Landlord / provider confirmation</option>
                </select>
                <input placeholder="Note for the reviewer (optional)" value={ver.evidence_note} onChange={(ev) => setVer({ ...ver, evidence_note: ev.target.value })} data-testid="rbr-verify-note" className={inputCls + " mt-2"} />
                <button onClick={() => act(() => api.post(`/rent-bill/${e.id}/request-verification`, ver), "Verification requested")} data-testid="rbr-request-verification-btn" className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#080d1a] px-4 py-2 font-head text-[13px] font-bold text-white hover:bg-[#1a5fd0]"><CheckCircle2 size={14} /> Request verification</button>
              </>
            )}
            {e.stage === "verification_pending" && (
              <div className="flex items-start gap-2 text-[12px] text-amber-700" data-testid="rbr-pending-note"><Clock size={14} className="mt-0.5 shrink-0" /> Under review by MMT. You'll see Verified or an Exception with a reason — we never auto-verify.</div>
            )}
            {["verified", "awaiting_provider"].includes(e.stage) && (
              <>
                <div className="text-[13px] font-semibold text-slate-800">Step 5 — Submit to reporting provider</div>
                <p className="mt-1 text-[12px] text-slate-500">{furnisherLive ? "A furnisher is connected — submission goes live." : "No reporting furnisher is connected yet. Submitting queues your verified history honestly as 'Awaiting reporting provider'."}</p>
                <button onClick={() => act(() => api.post(`/rent-bill/${e.id}/submit`), null)} data-testid="rbr-submit-btn" className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#1a5fd0] px-4 py-2 font-head text-[13px] font-bold text-white hover:bg-[#1550b8]"><Send size={14} /> {e.stage === "awaiting_provider" ? "Re-check provider" : "Submit"}</button>
                {e.stage === "awaiting_provider" && <div className="mt-2 text-[11px] text-amber-700" data-testid="rbr-awaiting-note">Queued since {String(e.submission?.attempted_at).slice(0, 10)} — not yet reported.</div>}
              </>
            )}
            {["submitted", "reported"].includes(e.stage) && <div className="text-[12px] text-emerald-700">{e.stage === "reported" ? "Confirmed reported by the provider." : "Submitted — awaiting provider confirmation."}</div>}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function RentBillReporting() {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(false);
  const { ent, reload: reloadEnt } = useEntitlements();
  const load = useCallback(() => api.get("/rent-bill/overview").then(({ data }) => setData(data)).catch(() => setData({ error: true })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  if (data.error) return <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-[13px] text-red-700">We couldn't load Rent & Bill Reporting.</div>;

  const locked = data.access.state === "upgrade_required";
  return (
    <div className="mx-auto max-w-4xl animate-fade-up pb-10" data-testid="rent-bill-page">
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Rent &amp; Bill <span className="text-foil">Reporting</span></h1>
      <p className="mt-1 max-w-2xl text-[14px] text-slate-500">Turn rent, utilities, phone and internet payments into reportable history. Every stage is a separate, honest event: <b>paid ≠ verified ≠ submitted ≠ reported ≠ score change</b>.</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <StatePill state={data.access.state} />
        <StatePill state={data.furnisher.state} labelOverride={data.furnisher.state === "connected" ? "Furnisher connected" : "Furnisher not available"} />
      </div>
      <p className="mt-2 text-[12px] text-slate-500" data-testid="rbr-furnisher-note">{data.furnisher.note}</p>

      {locked ? (
        <Card className="mt-6 p-6 text-center" data-testid="rbr-locked">
          <div className="font-head text-[16px] font-bold text-slate-900">Included with the {data.access.required_plan} plan</div>
          <p className="mt-1 text-[13px] text-slate-500">Upgrade to enroll recurring payments and start building verified history.</p>
          <button onClick={() => setModal(true)} data-testid="rbr-upgrade-btn" className="mt-4 rounded-lg bg-[#b0862f] px-5 py-2.5 font-head text-[13px] font-bold text-white hover:opacity-90">Upgrade Membership</button>
        </Card>
      ) : (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-4">
            <Stat label="Enrolled payments" value={data.counts.total} />
            <Stat label="Payments logged" value={data.counts.payments_logged} tone="blue" />
            <Stat label="Verified" value={data.counts.verified} tone="green" />
            <Stat label="Reported" value={data.counts.reported} sub={data.counts.reported ? "Provider confirmed" : "None yet — honest"} />
          </div>

          <Card className="mt-6 p-5" data-testid="rbr-enroll-card">
            <div className="font-head text-[15px] font-bold text-slate-900">Step 1 — Enroll a recurring payment</div>
            <p className="mb-3 mt-0.5 text-[12px] text-slate-500">Enrolling records the payment. It is not an authorization, a verification, or a report.</p>
            <EnrollForm types={data.types} onDone={load} />
          </Card>

          <div className="mt-6 space-y-4">
            {data.enrollments.length === 0 && <Card className="p-6 text-center text-[13px] text-slate-500" data-testid="rbr-empty">No recurring payments enrolled yet.</Card>}
            {data.enrollments.map((e) => <Enrollment key={e.id} e={e} furnisherLive={data.furnisher.state === "connected"} reload={load} />)}
          </div>
        </>
      )}

      <p className="mt-8 flex items-center justify-center gap-2 text-center text-[12px] text-slate-400"><ShieldCheck size={14} /> Reporting is confirmed only by a connected furnisher. MMT never marks a payment as reported on its own.</p>
      {modal && ent && <UpgradeModal open onClose={() => { setModal(false); reloadEnt(); load(); }} feature="rent_bill_reporting" requiredPlan={data.access.required_plan} returnRoute="/rent-bill-reporting" ent={ent} />}
    </div>
  );
}
